import {
  HomeIcon,
  BookOpenIcon,
  Squares2X2Icon,
  ChartBarSquareIcon,
  CalendarDaysIcon,
  TrophyIcon,
  AcademicCapIcon,
  UsersIcon,
  Cog6ToothIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";
import { GraduationCap } from "lucide-react";
import { AppId } from "@/app/globalSlices/appStripSlice";
import { AppStripItemConfig } from "./appStripTypes";

export interface AppStripMenuOption {
  label: string;
  href: string;
  description?: string;
}

/**
 * App Strip configuration
 *
 * Each item can have:
 * - permissions: Direct permission check (user needs ANY one)
 * - navItemInternalNames: Shows if ANY of these nav items are visible
 * - isCertainPathOnly: Only for CertainPath companies
 * - roles: Role-based visibility check (from labs)
 *
 * For sections (Resources, Stochastic, Admin), visibility is derived from
 * whether the user can see any nav items in that section.
 *
 * For external links (Scoreboard, Training, Partner Network), we use direct
 * permissions since they don't have child nav items.
 */
export const appStripItems: AppStripItemConfig[] = [
  {
    id: "home",
    name: "Home",
    icon: HomeIcon,
    defaultRoute: "/hub",
    // The member homepage opens on its own — no module sub-menu.
    standalone: true,
    navItemInternalNames: ["home"],
  },
  {
    id: "resources",
    name: "Resources",
    icon: BookOpenIcon,
    defaultRoute: "/hub/resources",
    // No permissions - Resources section items (home, resource_library, news, podcasts, help)
    // are generally accessible; specific items within have their own permissions
    navItemInternalNames: [
      "home",
      "resource_library",
      "news",
      "podcasts",
      "financial_reports",
    ],
  },
  {
    id: "stochastic",
    name: "Lead Generation",
    shortLabel: "Lead Gen",
    icon: Squares2X2Icon,
    defaultRoute: "/lead-generation/sales-and-customer-dashboard",
    // Visibility derived from stochastic nav items
    navItemInternalNames: [
      "dashboard",
      "roi_dashboard",
      "prospects",
      "customers",
      "invoices",
      "locations",
      "campaigns",
      "dashboards", // uploads parent
    ],
    roles: ["ROLE_SUPER_ADMIN", "ROLE_OWNER_GM", "ROLE_MARKETING"],
  },
  {
    id: "dashboards",
    name: "Data Connector",
    shortLabel: "Data",
    icon: ChartBarSquareIcon,
    defaultRoute: "/hub/data-connector",
    // Visibility based on data-connector permission
    navItemInternalNames: ["data_connector"],
    standalone: true,
    roles: ["ROLE_SUPER_ADMIN", "ROLE_OWNER_GM", "ROLE_COACH"],
  },
  {
    id: "events",
    name: "Events",
    icon: CalendarDaysIcon,
    defaultRoute: "/event-registration/events",
    // Visibility derived from event-related nav items
    navItemInternalNames: ["event-directory"],
    isCertainPathOnly: true,
    roles: ["ROLE_SUPER_ADMIN"],
  },
  {
    id: "product-storefront",
    name: "Product Store",
    shortLabel: "Store",
    icon: ShoppingCartIcon,
    roles: ["ROLE_SUPER_ADMIN"],
  },
  {
    id: "learning-center",
    name: "Learning Center",
    shortLabel: "Learning",
    icon: GraduationCap,
  },
  {
    id: "labs",
    name: "Labs",
    icon: LightBulbIcon,
    defaultRoute: "/games/door-to-done",
    navItemInternalNames: ["door_to_done_game"],
  },
  {
    id: "call-intelligence",
    name: "Call Intelligence",
    shortLabel: "Calls",
    icon: PhoneIcon,
    roles: [
      "ROLE_SUPER_ADMIN",
      "ROLE_SALES_MANAGER",
      "ROLE_SALES_REP",
      "ROLE_ADMIN",
      "ROLE_OPERATIONS",
      "ROLE_SUPPORT",
    ],
  },
  {
    id: "community",
    name: "Community",
    icon: ChatBubbleLeftRightIcon,
  },
  {
    id: "sales",
    name: "Sales",
    icon: CurrencyDollarIcon,
    roles: ["ROLE_SUPER_ADMIN", "ROLE_SALES_MANAGER", "ROLE_SALES_REP"],
  },
  {
    id: "scoreboard",
    name: "Scoreboard",
    icon: TrophyIcon,
    isExternal: true,
    externalUrl: process.env.REACT_APP_SCOREBOARD_URL || "",
    // Direct permission for external link -- any scoreboard permission grants access
    permissions: ["scoreboard", "scoreboard-admin", "scoreboard-coach"],
  },
  {
    id: "training",
    name: "Training",
    icon: AcademicCapIcon,
    isExternal: true,
    externalUrl: process.env.REACT_APP_MEMBER_PORTAL_URL || "",
    // Direct permission for external link -- any training-portal permission grants access
    permissions: ["training-portal", "training-portal-admin"],
  },
  {
    id: "partner-network-new",
    name: "Partner Network (New)",
    shortLabel: "Partners",
    icon: UsersIcon,
    roles: ["ROLE_SUPER_ADMIN", "ROLE_PARTNER_ADMIN"],
  },
  {
    id: "partner-network",
    name: "Partner Network (Legacy)",
    shortLabel: "Partners",
    icon: UsersIcon,
    isExternal: true,
    externalUrl: process.env.REACT_APP_PARTNER_NETWORK_URL || "",
    // Direct permission for external link -- any partner-network permission grants access
    permissions: ["partner-network", "partner-network-admin"],
  },
  {
    id: "admin",
    name: "Admin",
    icon: Cog6ToothIcon,
    defaultRoute: "/admin/events",
    // Visibility derived from admin nav items (both internal admin and company admin)
    navItemInternalNames: [
      // Internal Admin items (CertainPath employees)
      "event-registration-admin",
      "email_management",
      "resource_management",
      "user_management",
      "admin", // stochastic admin section
      // Company Admin items (company admins)
      "company_user_management",
      "company_settings",
    ],
    roles: [
      "ROLE_SUPER_ADMIN",
      "ROLE_MARKETING",
      "ROLE_OWNER_GM",
      "ROLE_LEARNING_ADMIN",
    ],
  },
];

// Navigation items that belong to Resources app
export const resourcesNavItems = [
  "home",
  "event-directory",
  "resource_library",
  "news",
  "podcasts",
  "document_library",
];

// Navigation items that belong to Stochastic app
export const stochasticNavItems = [
  "dashboard",
  "prospects",
  "customers",
  "invoices",
  "locations",
  "campaigns",
  "dashboards", // uploads parent
];

// Navigation items that belong to Dashboards app
export const dashboardsNavItems = ["data_connector"];

// Navigation items that belong to Labs app
export const labsNavItems = ["door_to_done_game"];

// Navigation items that belong to Learning Center app
export const learningCenterNavItems = [
  "learning_directory",
  "my_learning",
  "manager_team_progress",
  "coaches_learning",
  // Admin block (moved out of the Internal Admin gear, 2026-10-14)
  "course_management",
  "module_library",
  "manage_learning_paths",
  "manage_bundles",
  "custom_tasks",
  "badge_management",
  "quiz_management",
];

// Navigation items that belong to Partner Network app
export const partnerNetworkNavItems = [
  "dashboard",
  "partners",
  "members",
  "registrations",
  "quarter_status",
  "transactions",
  "member_rebates",
  "reports",
  "management",
  "admin_partners",
  "admin_analytics",
  "admin_sync",
];

// Navigation items that belong to Coaching app
export const coachingNavItems = ["coaching_dashboard"];

// Navigation items that belong to Community app
export const communityNavItems = ["community"];

// Navigation items that belong to Product Store app
export const productStorefrontNavItems = ["product-directory"];

// Navigation items that belong to Admin app (includes all admin sections)
export const adminNavItems = [
  "event-registration-admin",
  "email_management",
  "resource_management",
  "user_management",
  "admin", // stochastic admin
  "games",
  "capex",
  "product_storefront_admin",
];

// Navigation items that belong to Sales app
export const salesNavItems = ["customer_pipeline"];
